/**
 * BranchContext — global "currently active branch".
 * Persisted to localStorage so navigation/reloads keep the selection.
 *
 * The currentBranchId is also forwarded to the backend on every request via
 * axios interceptor + getAuthHeaders() (header: X-Active-Branch-Id).
 *
 * Setting branch=null  → "All branches" (default = company-wide view).
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import axios from 'axios';
import { API_URL, getAuthHeaders } from '../config';

const STORAGE_KEY = 'active_branch_id';
const BranchContext = createContext(null);

export const BranchProvider = ({ children }) => {
  const [allBranches, setAllBranches] = useState([]);
  const [currentBranchId, setCurrentBranchId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ''
  );
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAllBranches([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/branches`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      setAllBranches(Array.isArray(data) ? data : []);
    } catch {
      setAllBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload when the auth token changes (login → fetch; logout → clear).
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'token') refresh();
    };
    window.addEventListener('storage', onStorage);
    refresh();
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  // Mirror current branch id into axios defaults so older code that uses bare
  // axios still ships the header.
  useEffect(() => {
    if (currentBranchId) {
      axios.defaults.headers.common['X-Active-Branch-Id'] = currentBranchId;
      localStorage.setItem(STORAGE_KEY, currentBranchId);
    } else {
      delete axios.defaults.headers.common['X-Active-Branch-Id'];
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [currentBranchId]);

  const currentBranch = useMemo(
    () => allBranches.find((b) => b.id === currentBranchId) || null,
    [allBranches, currentBranchId]
  );

  const setBranch = useCallback((id) => {
    setCurrentBranchId(id || '');
  }, []);

  const value = useMemo(
    () => ({ allBranches, currentBranchId, currentBranch, loading, setBranch, refresh }),
    [allBranches, currentBranchId, currentBranch, loading, setBranch, refresh],
  );

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

export const useBranch = () => {
  const ctx = useContext(BranchContext);
  if (!ctx) {
    // Return a safe no-op when provider is not mounted (e.g. tests).
    return {
      allBranches: [], currentBranchId: '', currentBranch: null,
      loading: false, setBranch: () => {}, refresh: () => {},
    };
  }
  return ctx;
};

export default BranchContext;
